import { useDefilement } from '@/lib/ui/useDefilement'

interface Tab {
  id: string
  label: string
}

interface Props {
  tabs: Tab[]
  active: string
  onChange: (id: string) => void
}

/**
 * Onglets soulignés — 06 §6.3, [DECISIONS §147].
 *
 * Groupe `tablist` correctement étiqueté : les onglets ne sont pas des `div`
 * cliquables. C'était l'erreur de 2022 sur les choix de réponse, et elle
 * rendait l'application inutilisable au clavier.
 *
 * **Ils se masquent en descendant et reviennent en remontant.** Descendre,
 * c'est vouloir du contenu ; remonter, c'est chercher une commande. Un menu
 * secondaire qui occupe en permanence sa bande vole au contenu le sixième de la
 * hauteur d'un téléphone, en comptant l'en-tête au-dessus.
 *
 * **Un seul composant, donc la règle vaut partout** — Fil, Thèmes, Formations,
 * Archives, Classement, Mon Cercle, Notifications, banque d'exercices. C'est
 * l'intérêt d'avoir un `Tabs` et non huit barres écrites à la main : la
 * cohérence n'est pas une consigne à répéter, c'est une propriété du composant.
 *
 * **Ils restent dans le flux quand ils se masquent** (`translate`, pas
 * `display`) : le contenu ne remonte pas d'un cran sous le doigt, ce qui
 * déplacerait la ligne qu'on est en train de lire.
 */
export function Tabs({ tabs, active, onChange }: Props) {
  const visible = useDefilement()

  return (
    <div
      role="tablist"
      // `--haut-entete` est posée par l'AppShell, qui MESURE son en-tête : les
      // onglets se collent dessous sans qu'aucune hauteur soit recopiée ici,
      // et suivent l'en-tête s'il change.
      className="sticky z-[9] flex border-b backdrop-blur-md transition-transform duration-200"
      style={{
        top: 'var(--haut-entete, 0px)',
        borderColor: 'var(--bordure)',
        background: 'color-mix(in srgb, var(--fond) 92%, transparent)',
        // Vers le HAUT, et de sa propre hauteur : la barre disparaît sous
        // l'en-tête au lieu de glisser par-dessus le contenu.
        transform: visible ? 'none' : 'translateY(-100%)',
      }}
    >
      {tabs.map((tab) => {
        const selected = tab.id === active

        return (
          <button
            key={tab.id}
            role="tab"
            type="button"
            aria-selected={selected}
            // Masquée, la barre sort aussi du parcours au clavier : une cible
            // qu'on ne voit pas ne doit pas pouvoir recevoir le focus.
            tabIndex={visible ? 0 : -1}
            onClick={() => { onChange(tab.id); }}
            className="relative flex-1 py-4 text-[15px] font-bold transition-colors"
            style={{ color: selected ? 'var(--texte-1)' : 'var(--texte-2)' }}
          >
            {tab.label}
            {selected && (
              <span
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 mx-auto h-1 w-14 rounded-full"
                style={{ background: 'var(--accent)' }}
              />
            )}
          </button>
        )
      })}
    </div>
  )
}
